import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import SiteMeta from "./components/common/SiteMeta";
import OrgSchema from "./components/common/OrgSchema";
import TrackingScripts from "./components/common/TrackingScripts";

import "./App.css";
import { AuthProvider } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import RedirectsHandler from "./components/common/RedirectsHandler";

import SiteLayout from "./components/layout/SiteLayout";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import { BusinessesListPage, BusinessDetailPage } from "./pages/BusinessesPage";
import { ProjectsListPage, ProjectDetailPage } from "./pages/ProjectsPage";
import { MediaListPage, NewsDetailPage } from "./pages/MediaPage";
import { CompaniesListPage, CompanyDetailPage } from "./pages/CompaniesPage";
import CareersPage from "./pages/CareersPage";
import ContactPage from "./pages/ContactPage";
import GovernancePage from "./pages/GovernancePage";
import PartnerProgramPage from "./pages/PartnerProgramPage";
import LegalPage from "./pages/LegalPage";
import DynamicPage from "./pages/DynamicPage";
import RealEstateLandingPage from "./pages/RealEstateLandingPage";
import YenisehirPage from "./pages/YenisehirPage";
import YenisehirProjectsPage from "./pages/YenisehirProjectsPage";
import YenisehirProjectDetailPage from "./pages/YenisehirProjectDetailPage";

import AdminLayout from "./components/admin/AdminLayout";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import LoginPage from "./pages/admin/LoginPage";
import DashboardPage from "./pages/admin/DashboardPage";
import AdminHomepagePage from "./pages/admin/HomepagePage";
import AdminPagesPage from "./pages/admin/PagesPage";
import AdminMenusPage from "./pages/admin/MenusPage";
import AdminBusinessUnitsPage from "./pages/admin/BusinessUnitsPage";
import AdminProjectsPage from "./pages/admin/ProjectsPage";
import AdminNewsPage from "./pages/admin/NewsPage";
import AdminMediaPage from "./pages/admin/MediaPage";
import AdminContactSubmissionsPage from "./pages/admin/ContactSubmissionsPage";
import AdminCareersPage from "./pages/admin/CareersPage";
import AdminGroupCompaniesPage from "./pages/admin/GroupCompaniesPage";
import AdminTeamPage from "./pages/admin/TeamPage";
import AdminTestimonialsPage from "./pages/admin/TestimonialsPage";
import AdminCallbacksPage from "./pages/admin/CallbacksPage";
import AdminRedirectsPage from "./pages/admin/RedirectsPage";
import AdminSettingsPage from "./pages/admin/SettingsPage";
import AdminLogosPage from "./pages/admin/LogosPage";
import AdminUsersPage from "./pages/admin/UsersPage";
import AdminSeoAnalysisPage from "./pages/admin/SeoAnalysisPage";
import AdminYenisehirProjectsPage from "./pages/admin/YenisehirProjectsPage";
import AdminNavMenuPage from "./pages/admin/NavMenuPage";
import AdminLandingPagesPage from "./pages/admin/LandingPagesPage";
import EstateOSHome from "./pages/EstateOSHome";
import EstateCRMLayout from "./components/EstateCRMLayout";
import LeadsKanbanPage from "./pages/estateos/LeadsKanbanPage";
import EstateUsersPage from "./pages/estateos/EstateUsersPage";
import EstateRolesPage from "./pages/estateos/EstateRolesPage";

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <SiteMeta />
            <OrgSchema />
            <TrackingScripts />
            <RedirectsHandler />
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Public site */}
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/governance" element={<GovernancePage />} />
                <Route path="/careers/is-ortagim-programi" element={<PartnerProgramPage />} />
                <Route path="/businesses" element={<BusinessesListPage />} />
                <Route path="/businesses/:slug" element={<BusinessDetailPage />} />
                <Route path="/projects" element={<ProjectsListPage />} />
                <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                <Route path="/media" element={<MediaListPage />} />
                <Route path="/media/:slug" element={<NewsDetailPage />} />
                <Route path="/companies" element={<CompaniesListPage />} />
                <Route path="/companies/:slug" element={<CompanyDetailPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="/p/:slug" element={<DynamicPage />} />
              </Route>

              {/* Real Estate Landing Page (standalone — no SiteLayout, own header/footer) */}
              <Route path="/realestate" element={<RealEstateLandingPage />} />
              <Route path="/yenisehir" element={<YenisehirPage />} />
              <Route path="/yenisehir-projeleri" element={<YenisehirProjectsPage />} />
              <Route path="/yenisehir-projeleri/:slug" element={<YenisehirProjectDetailPage />} />

              {/* Admin */}
              <Route path="/admin/login" element={<LoginPage /> } />

              {/* EstateOS dedicated login (skips portal chooser) */}
              <Route path="/estateos/login" element={<LoginPage initialPanel="estateos" hideChooser />} />

              {/* EstateOS suite (protected) */}
              <Route
                path="/estateos"
                element={
                  <ProtectedRoute>
                    <EstateOSHome />
                  </ProtectedRoute>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <EstateCRMLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/estateos/crm" element={<Navigate to="/estateos/crm/leads" replace />} />
                <Route path="/estateos/crm/leads" element={<LeadsKanbanPage />} />
                <Route path="/estateos/users" element={<EstateUsersPage />} />
                <Route path="/estateos/roles" element={<EstateRolesPage />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="homepage" element={<AdminHomepagePage />} />
                <Route path="pages" element={<AdminPagesPage />} />
                <Route path="menus" element={<AdminMenusPage />} />
                <Route path="business-units" element={<AdminBusinessUnitsPage />} />
                <Route path="projects" element={<AdminProjectsPage />} />
                <Route path="news" element={<AdminNewsPage />} />
                <Route path="media" element={<AdminMediaPage />} />
                <Route path="contact-submissions" element={<AdminContactSubmissionsPage />} />
                <Route path="career-openings" element={<AdminCareersPage />} />
                <Route path="group-companies" element={<AdminGroupCompaniesPage />} />
                <Route path="team" element={<AdminTeamPage />} />
                <Route path="testimonials" element={<AdminTestimonialsPage />} />
                <Route path="callbacks" element={<AdminCallbacksPage />} />
                <Route path="redirects" element={<AdminRedirectsPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="logos" element={<AdminLogosPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="seo-analysis" element={<AdminSeoAnalysisPage />} />
                <Route path="yenisehir-projects" element={<AdminYenisehirProjectsPage />} />
                <Route path="nav-menu" element={<AdminNavMenuPage />} />
                <Route path="landing-pages" element={<AdminLandingPagesPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
